// TaskHubDashboard.tsx
import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@prio365/prio365-react-library';
import Flex from '../../components/Flex';
import { makePrioStyles } from '../../theme/utils';
import { TaskSummaryDto } from './TaskHubModule';
import TaskHubTable from './TaskHubTable';
import { AssigneeBarChart } from './components/AssigneeBarChart';
import { ProjectBarChart } from './components/ProjectBarChart';
import {
  TaskHubDashboardSkeleton,
  SummaryCardsSkeleton,
} from './TaskHubSkeletons';
import { ExtendedTaskDto } from './api/taskHubApi';
import CollapsibleFilters from './CollapsibleFilters';

const useStyles = makePrioStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.regular,
  },
  bottomRow: {
    display: 'flex',
    gap: theme.spacing.regular,
    width: '100%',
    overflow: 'hidden',
  },
  fullWidthTable: {
    width: '100%',
    overflow: 'hidden',
    height: '400px',
  },
  errorContainer: {
    marginTop: theme.spacing.regular,
    padding: theme.spacing.regular,
    backgroundColor: theme.colors.base.red['20'],
    borderRadius: theme.borderRadius.regular,
    border: `1px solid ${theme.colors.base.red.default}`,
  },
  errorTitle: {
    fontWeight: 600,
    color: theme.colors.base.red.default,
    marginBottom: theme.spacing.small,
  },
  errorMessage: {
    color: theme.colors.base.red.default,
    fontSize: '14px',
  },
  summaryCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: theme.spacing.regular,
    marginBottom: theme.spacing.regular,
  },
  summaryCard: {
    padding: theme.spacing.regular,
    backgroundColor: theme.colors.application.background.default,
    borderRadius: theme.borderRadius.regular,
    border: `1px solid ${theme.colors.application.border}`,
    textAlign: 'center',
  },
  summaryNumber: {
    fontSize: '32px',
    fontWeight: 700,
    color: theme.colors.base.primary.default,
    lineHeight: 1,
    marginBottom: theme.spacing.small,
  },
  summaryLabel: {
    fontSize: '14px',
    color: theme.colors.application.typography.muted,
    fontWeight: 500,
  },
}));

interface TaskHubDashboardProps {
  summary: TaskSummaryDto;
  allTasks: ExtendedTaskDto[];
  onChartInteraction: (filterType: string, filterValue: string) => void;
  onTaskDetail: (task: ExtendedTaskDto) => void;
  loading?: boolean;
  initialLoad?: boolean;
  getContactById?: (
    id: string
  ) => { firstName: string; lastName: string } | null;
}

interface Filters {
  assignee?: string[];
  project?: string[];
  priority?: string[];
  labels?: string[];
  status?: string[];
  dueDate?: string[];
  createdBy?: string[];
}

const TaskHubDashboard: React.FC<TaskHubDashboardProps> = ({
  summary,
  allTasks = [],
  onChartInteraction,
  onTaskDetail,
  loading = false,
  initialLoad = false,
  getContactById,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  // Filter state
  const [filters, setFilters] = useState<Filters>({});
  const [filtersLoading, setFiltersLoading] = useState(false);

  // Helper functions
  const getStatusLabel = useCallback(
    (status: string): string => {
      switch (status.toLowerCase()) {
        case 'notstarted':
          return t('taskHub:table.status.notStarted');
        case 'inprogress':
          return t('taskHub:table.status.inProgress');
        case 'completed':
          return t('taskHub:table.status.completed');
        default:
          return status;
      }
    },
    [t]
  );

  const getPriorityLabel = useCallback(
    (priority: string): string => {
      switch (priority.toLowerCase()) {
        case 'high':
          return t('taskHub:table.priority.high');
        case 'medium':
          return t('taskHub:table.priority.medium');
        case 'low':
          return t('taskHub:table.priority.low');
        case 'urgent':
          return t('taskHub:table.priority.urgent');
        case 'important':
          return t('taskHub:table.priority.important');
        default:
          return priority.charAt(0).toUpperCase() + priority.slice(1);
      }
    },
    [t]
  );

  // Helper function to resolve contact name
  const getContactName = useCallback(
    (contactId: string): string => {
      if (!getContactById) return contactId;

      const contact = getContactById(contactId);
      return contact ? `${contact.firstName} ${contact.lastName}` : contactId;
    },
    [getContactById]
  );

  // Helper function to check task due date status
  const getTaskDueDateStatus = useCallback((task: ExtendedTaskDto): string => {
    if (!task.dueDate) return 'noDueDate';

    const today = new Date();
    const dueDate = new Date(task.dueDate);

    // Reset time to compare dates only
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);

    if (dueDate < today) {
      return 'overdue';
    }

    // Check if due date is within this week
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (7 - today.getDay()));

    if (dueDate <= endOfWeek) {
      return 'thisWeek';
    }

    return 'future';
  }, []);

  // Generate filter options from tasks
  const filterOptions = useMemo(() => {
    const assignees = new Set<string>();
    const projects = new Set<string>();
    const priorities = new Set<string>();
    const labels = new Set<string>();
    const statuses = new Set<string>();
    const createdBySet = new Set<string>();

    allTasks.forEach((task) => {
      // Assignees
      if (task.assignees.length > 0) {
        task.assignees.forEach((assignee) => assignees.add(assignee));
      } else {
        assignees.add('Unassigned');
      }

      // Projects
      projects.add(task.projectDisplayName || 'Unknown Project');

      // Created By
      if (task.createdBy) {
        const createdByName = getContactName(task.createdBy);
        createdBySet.add(createdByName);
      }

      // Other fields
      priorities.add(task.priority);
      statuses.add(task.status);
      task.labels.forEach((label) => labels.add(label));
    });

    // Due date filter options
    const dueDateOptions = [
      {
        value: 'overdue',
        label: t('taskHub:dashboard.filters.dueDateOptions.overdue'),
      },
      {
        value: 'thisWeek',
        label: t('taskHub:dashboard.filters.dueDateOptions.thisWeek'),
      },
      {
        value: 'noDueDate',
        label: t('taskHub:dashboard.filters.dueDateOptions.noDueDate'),
      },
    ];

    return {
      assignees: Array.from(assignees)
        .sort()
        .map((assignee) => ({
          value: assignee,
          label:
            assignee === 'Unassigned'
              ? t('taskHub:table.unassigned')
              : assignee,
        })),
      projects: Array.from(projects)
        .sort()
        .map((project) => ({
          value: project,
          label: project,
        })),
      priorities: Array.from(priorities)
        .sort()
        .map((priority) => ({
          value: priority,
          label: getPriorityLabel(priority),
        })),
      labels: Array.from(labels)
        .sort()
        .map((label) => ({
          value: label,
          label: label,
        })),
      statuses: Array.from(statuses)
        .sort()
        .map((status) => ({
          value: status,
          label: getStatusLabel(status),
        })),
      dueDates: dueDateOptions,
      createdBy: Array.from(createdBySet)
        .sort()
        .map((creator) => ({
          value: creator,
          label: creator,
        })),
    };
  }, [allTasks, t, getPriorityLabel, getStatusLabel, getContactName]);

  // Filter tasks based on current filters
  const filteredTasks = useMemo(() => {
    return allTasks.filter((task) => {
      if (filters.assignee && filters.assignee.length > 0) {
        const taskAssignees =
          task.assignees.length > 0 ? task.assignees : ['Unassigned'];
        if (
          !filters.assignee.some((assignee) => taskAssignees.includes(assignee))
        ) {
          return false;
        }
      }

      if (filters.project && filters.project.length > 0) {
        const taskProjectName = task.projectDisplayName || 'Unknown Project';
        if (!filters.project.includes(taskProjectName)) {
          return false;
        }
      }

      if (filters.priority && filters.priority.length > 0) {
        if (!filters.priority.includes(task.priority)) {
          return false;
        }
      }

      if (filters.labels && filters.labels.length > 0) {
        if (!filters.labels.some((label) => task.labels.includes(label))) {
          return false;
        }
      }

      if (filters.status && filters.status.length > 0) {
        if (!filters.status.includes(task.status)) {
          return false;
        }
      }

      // Due date filter
      if (filters.dueDate && filters.dueDate.length > 0) {
        const taskDueDateStatus = getTaskDueDateStatus(task);
        if (!filters.dueDate.includes(taskDueDateStatus)) {
          return false;
        }
      }

      // Created by filter
      if (filters.createdBy && filters.createdBy.length > 0) {
        if (!task.createdBy) {
          return false;
        }
        const taskCreatedByName = getContactName(task.createdBy);
        if (!filters.createdBy.includes(taskCreatedByName)) {
          return false;
        }
      }

      return true;
    });
  }, [allTasks, filters, getTaskDueDateStatus, getContactName]);

  // Calculate unfiltered summary for charts
  const chartSummary = useMemo(() => {
    const summary: TaskSummaryDto = {
      totalTasks: allTasks.length,
      completedTasks: allTasks.filter((t) => t.status === 'completed').length,
      overdueTasks: allTasks.filter(
        (t) => getTaskDueDateStatus(t) === 'overdue'
      ).length,
      tasksByStatus: {},
      tasksByPriority: {},
      tasksByAssignee: {},
      tasksByProject: {},
    };

    allTasks.forEach((task) => {
      summary.tasksByStatus[task.status] =
        (summary.tasksByStatus[task.status] || 0) + 1;

      if (task.assignees.length > 0) {
        task.assignees.forEach((assignee) => {
          summary.tasksByAssignee[assignee] =
            (summary.tasksByAssignee[assignee] || 0) + 1;
        });
      } else {
        summary.tasksByAssignee['Unassigned'] =
          (summary.tasksByAssignee['Unassigned'] || 0) + 1;
      }

      const projectName = task.projectDisplayName || 'Unknown Project';
      summary.tasksByProject[projectName] =
        (summary.tasksByProject[projectName] || 0) + 1;
    });

    return summary;
  }, [allTasks, getTaskDueDateStatus]);

  // Calculate filtered summary for display
  const filteredSummary = useMemo(() => {
    const completed = filteredTasks.filter(
      (t) => t.status === 'completed'
    ).length;
    const overdue = filteredTasks.filter(
      (t) => getTaskDueDateStatus(t) === 'overdue'
    ).length;

    return {
      total: filteredTasks.length,
      completed,
      overdue,
      inProgress: filteredTasks.filter((t) => t.status === 'InProgress').length,
      notStarted: filteredTasks.filter((t) => t.status === 'NotStarted').length,
    };
  }, [filteredTasks, getTaskDueDateStatus]);

  // Handle filter changes
  const handleFilterChange = useCallback(
    (filterType: string, values: string[]) => {
      setFiltersLoading(true);
      setTimeout(() => {
        setFilters((prev) => ({
          ...prev,
          [filterType]: values.length > 0 ? values : undefined,
        }));
        setFiltersLoading(false);
      }, 100);
    },
    []
  );

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setFiltersLoading(true);
    setTimeout(() => {
      setFilters({});
      setFiltersLoading(false);
    }, 100);
  }, []);

  const handleAssigneeChartClick = useCallback(
    (assigneeName: string) => {
      setFilters((prev) => {
        const currentAssignees = prev.assignee || [];

        const newAssignees = currentAssignees.includes(assigneeName)
          ? currentAssignees.filter((a) => a !== assigneeName)
          : [...currentAssignees, assigneeName];

        return {
          ...prev,
          assignee: newAssignees.length > 0 ? newAssignees : undefined,
        };
      });
      onChartInteraction('assignee', assigneeName);
    },
    [onChartInteraction]
  );

  const handleProjectChartClick = useCallback(
    (projectName: string) => {
      setFilters((prev) => {
        const currentProjects = prev.project || [];

        const newProjects = currentProjects.includes(projectName)
          ? currentProjects.filter((p) => p !== projectName)
          : [...currentProjects, projectName];

        return {
          ...prev,
          project: newProjects.length > 0 ? newProjects : undefined,
        };
      });
      onChartInteraction('project', projectName);
    },
    [onChartInteraction]
  );

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some((filter) => filter && filter.length > 0);
  }, [filters]);

  // Loading states
  if (initialLoad || loading) {
    return <TaskHubDashboardSkeleton />;
  }

  return (
    <div className={classes.root}>
      {/* Collapsible Filters */}
      <CollapsibleFilters
        filters={filters}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        onClearAllFilters={clearAllFilters}
        filtersLoading={filtersLoading}
        initiallyExpanded={false}
      />

      {/* Charts Row */}
      <div className={classes.bottomRow}>
        <Flex.Column flex={1}>
          <AssigneeBarChart
            summary={chartSummary}
            allTasks={allTasks}
            onAssigneeClick={handleAssigneeChartClick}
            selectedAssignees={filters.assignee || []}
            maxItems={15}
          />
        </Flex.Column>

        <Flex.Column flex={1}>
          <ProjectBarChart
            summary={chartSummary}
            allTasks={allTasks}
            onProjectClick={handleProjectChartClick}
            selectedProjects={filters.project || []}
            maxItems={15}
          />
        </Flex.Column>
      </div>

      {/* Full Width Table */}
      <div className={classes.fullWidthTable}>
        <TaskHubTable
          tasks={filteredTasks}
          onTaskDetail={onTaskDetail}
          loading={filtersLoading}
          title={
            hasActiveFilters
              ? t('taskHub:table.filteredTasks')
              : t('taskHub:table.allTasks')
          }
        />
      </div>

      {/* Summary Cards */}
      {filtersLoading ? (
        <SummaryCardsSkeleton />
      ) : (
        <div className={classes.summaryCards}>
          <div className={classes.summaryCard}>
            <div className={classes.summaryNumber}>{filteredSummary.total}</div>
            <div className={classes.summaryLabel}>
              {t('taskHub:dashboard.summary.totalTasks')}
            </div>
          </div>
          <div className={classes.summaryCard}>
            <div
              className={classes.summaryNumber}
              style={{ color: theme.colors.base.black.default }}
            >
              {filteredSummary.notStarted}
            </div>
            <div className={classes.summaryLabel}>
              {t('taskHub:dashboard.summary.notStarted')}
            </div>
          </div>
          <div className={classes.summaryCard}>
            <div
              className={classes.summaryNumber}
              style={{ color: theme.colors.base.yellow.default }}
            >
              {filteredSummary.inProgress}
            </div>
            <div className={classes.summaryLabel}>
              {t('taskHub:dashboard.summary.inProgress')}
            </div>
          </div>
          <div className={classes.summaryCard}>
            <div
              className={classes.summaryNumber}
              style={{ color: theme.colors.base.red.default }}
            >
              {filteredSummary.overdue}
            </div>
            <div className={classes.summaryLabel}>
              {t('taskHub:dashboard.summary.overdue')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskHubDashboard;
